/**
 * F1 / VISUAL_QA_R22_DELAY_TO_FILE + F3a / VISUAL_QA_R23_DELAY
 *
 * delayToFile is the first revision-file GET, not /tree and not Think hydrate.
 *
 * F3a: when the URL already names a rev (`:revId` or `?rev=`), start that GET
 * on first paint / route parse — do not wait for GET /hardware.
 *
 * F1: when the listing picks the rev, start the GET in the same synchronous
 * turn as hardware / home JSON resolves — before setActive / React commit.
 *
 * One entry per (revision, file). URL-known (rev, file) is F3a only; F1
 * passes filePath: null in that case. Inspect consumes the prefetch Promise.
 */

import { prefetchRevisionFile } from "./files-viewer-open.ts";
import {
  fileUrl,
  knownRevisionIdFromRoute,
  revIdFromFilesPath,
  revIdFromSearch,
} from "./kicad-path.ts";
import { isKicadViewerPath } from "./kicanvas-sources.ts";
import { viewerAutoloadAllowed } from "./kicanvas-viewer-load.ts";

export { knownRevisionIdFromRoute, revIdFromFilesPath, revIdFromSearch };

export function pickActiveRevisionId(
  knownRevId: string | null | undefined,
  revisions: ReadonlyArray<{ id: string }> | null | undefined,
): string | null {
  const known = typeof knownRevId === "string" ? knownRevId.trim() : "";
  const ids = (revisions ?? []).map((row) => row.id).filter((id) => typeof id === "string" && id.trim());
  if (known && (ids.length === 0 || ids.includes(known))) return known;
  return ids[0] ?? null;
}

/** Hosted revision id from `:revId`, `/files/revisions/:revId`, or `?rev=`. */
export function filesUrlRevisionId(opts: {
  routeRevId?: string | null;
  pathname?: string | null;
  search?: string | null;
}): string | null {
  return knownRevisionIdFromRoute(opts);
}

/** Map URL rev + listing to the revision the Files page shows; surface unknown URL revs. */
export function resolveHostedRevisionDisplay(
  urlRevId: string | null,
  revisions: ReadonlyArray<{ id: string }> | null | undefined,
): { activeRevId: string | null; unknownUrlRev: string | null } {
  const activeRevId = pickActiveRevisionId(urlRevId, revisions);
  const url = typeof urlRevId === "string" ? urlRevId.trim() : "";
  const ids = (revisions ?? []).map((row) => row.id);
  if (url && ids.length > 0 && !ids.includes(url) && activeRevId) {
    return { activeRevId, unknownUrlRev: url };
  }
  return { activeRevId, unknownUrlRev: null };
}

/** Revision-file href once org+proj+rev+file are known. No loading gate. */
export function revisionFilePrefetchHref(opts: {
  orgSlug?: string | null;
  projectSlug?: string | null;
  activeRevId?: string | null;
  filePath?: string | null;
  /** Catalog `byte_size` for `filePath`; omit to allow prefetch (size unknown). */
  catalogByteSize?: number | null;
}): string | null {
  const org = opts.orgSlug?.trim() ?? "";
  const proj = opts.projectSlug?.trim() ?? "";
  const rev = opts.activeRevId?.trim() ?? "";
  const file = opts.filePath?.trim() ?? "";
  if (!org || !proj || !rev || !file) return null;
  if (!isKicadViewerPath(file)) return null;
  if (!viewerAutoloadAllowed(opts.catalogByteSize)) return null;
  return fileUrl(org, proj, rev, file);
}

/**
 * Kick the sheet GET now. Call this on the JSON resolve edge, before setState.
 * Home LIMIT 5 must pass a known rev here — do not re-pick against that list.
 */
export function prefetchKnownRevisionFile(
  opts: {
    orgSlug?: string | null;
    projectSlug?: string | null;
    activeRevId?: string | null;
    filePath?: string | null;
    catalogByteSize?: number | null;
  },
  fetchImpl: typeof fetch = fetch,
): string | null {
  const href = revisionFilePrefetchHref(opts);
  if (href) prefetchRevisionFile(href, fetchImpl);
  return href;
}

/**
 * F3a first-paint / route-parse edge. Call this while parsing `:revId` or
 * `?rev=` + `?file=` — before getHardware, tree, Think, or Promise.all.
 * Listing-picked rev stays on applyHardwareRevisionPrefetch (F1).
 */
export function prefetchKnownRevisionFromRoute(
  opts: {
    orgSlug?: string | null;
    projectSlug?: string | null;
    routeRevId?: string | null;
    pathname?: string | null;
    search?: string | null;
    filePath?: string | null;
    catalogByteSize?: number | null;
  },
  fetchImpl: typeof fetch = fetch,
): string | null {
  const activeRevId = knownRevisionIdFromRoute(opts);
  if (!activeRevId) return null;
  return prefetchKnownRevisionFile(
    {
      orgSlug: opts.orgSlug,
      projectSlug: opts.projectSlug,
      activeRevId,
      filePath: opts.filePath,
      catalogByteSize: opts.catalogByteSize,
    },
    fetchImpl,
  );
}

export type HardwareRevisionPrefetchResult = {
  activeRevId: string | null;
  prefetchHref: string | null;
};

/**
 * GET /hardware resolve edge: pick the rev, start the sheet GET, then the
 * caller may setActive. Prefetch is not deferred to the next React commit.
 */
export function applyHardwareRevisionPrefetch(
  opts: {
    orgSlug?: string | null;
    projectSlug?: string | null;
    knownRevId?: string | null;
    filePath?: string | null;
    revisions: ReadonlyArray<{ id: string }> | null | undefined;
  },
  fetchImpl: typeof fetch = fetch,
): HardwareRevisionPrefetchResult {
  const activeRevId = pickActiveRevisionId(opts.knownRevId, opts.revisions);
  const prefetchHref = prefetchKnownRevisionFile(
    {
      orgSlug: opts.orgSlug,
      projectSlug: opts.projectSlug,
      activeRevId,
      filePath: opts.filePath,
    },
    fetchImpl,
  );
  return { activeRevId, prefetchHref };
}
